import { useState } from "react";
import { isCreatedOrderResponse } from "../lib/catalog";
import { storeOrderAccessToken } from "./useCustomerOrder";
import type { CartLine, CheckoutForm, OrderSummary } from "../types/catalog";
import type { CreatedOrderResponse } from "../lib/catalog";

type UseCheckoutOptions = {
  cartItems: CartLine[];
  cartTotalKes: number;
  onCartCleared: () => void;
  onCheckoutComplete: (order: CreatedOrderResponse) => void;
  onOrderCreated: (order: OrderSummary) => void;
  onCustomerProfileRemembered: (checkout: CheckoutForm) => void;
};

export function useCheckout({
  cartItems,
  cartTotalKes,
  onCartCleared,
  onCheckoutComplete,
  onCustomerProfileRemembered,
  onOrderCreated,
}: UseCheckoutOptions) {
  const [checkoutStatus, setCheckoutStatus] = useState("");
  const [checkoutStatusTone, setCheckoutStatusTone] = useState<"error" | "neutral" | "success">("neutral");
  const [isCheckoutSubmitting, setIsCheckoutSubmitting] = useState(false);

  async function handleCheckout(checkout: CheckoutForm) {
    if (cartItems.length === 0) {
      setCheckoutStatus("Your cart is empty. Add a part before checkout.");
      setCheckoutStatusTone("error");
      return;
    }

    if (isCheckoutSubmitting) {
      return;
    }

    setIsCheckoutSubmitting(true);
    setCheckoutStatus("Creating your order...");
    setCheckoutStatusTone("neutral");

    try {
      const response = await fetch("/api/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customerName: checkout.customerName,
          customerEmail: checkout.customerEmail,
          phone: checkout.phone,
          fulfillmentMethod: checkout.fulfillmentMethod,
          location: checkout.location,
          notes: checkout.notes,
          items: cartItems.map((item) => ({
            productId: item.product.id,
            quantity: item.quantity,
          })),
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json<{ error?: string }>().catch(() => null);
        setCheckoutStatus(errorBody?.error ?? "Order could not be created. Please check your details and try again.");
        setCheckoutStatusTone("error");
        return;
      }

      const body: unknown = await response.json();
      const createdOrder = isCreatedOrderResponse(body)
        ? body
        : { orderId: crypto.randomUUID(), totalKes: cartTotalKes };

      onOrderCreated({
        id: createdOrder.orderId,
        orderCode: createdOrder.orderCode,
        customerName: checkout.customerName,
        customerEmail: checkout.customerEmail,
        phone: checkout.phone,
        status: "pending_payment",
        paymentStatus: "pending",
        totalKes: createdOrder.totalKes,
        itemCount: cartItems.reduce((total, item) => total + item.quantity, 0),
      });

      if (createdOrder.orderCode && createdOrder.customerAccessToken) {
        storeOrderAccessToken(createdOrder.orderCode, createdOrder.customerAccessToken);
      }

      setCheckoutStatus("Order created. Opening payment...");
      setCheckoutStatusTone("success");
      onCustomerProfileRemembered(checkout);
      onCartCleared();
      onCheckoutComplete(createdOrder);
    } catch {
      setCheckoutStatus("Order could not be created because the network request failed. Please try again.");
      setCheckoutStatusTone("error");
    } finally {
      setIsCheckoutSubmitting(false);
    }
  }

  return {
    checkoutStatus,
    checkoutStatusTone,
    handleCheckout,
    isCheckoutSubmitting,
  };
}
